import "bootstrap/dist/css/bootstrap.min.css"
import "./src/styles/global.scss"
import "./src/styles/variable.scss"
import "@fontsource/poppins"
import NProgress from "accessible-nprogress"

const YANDEX_METRICA_ID = Number(
  process.env.GATSBY_YANDEX_METRICA_ID || "95012441",
)

const normalizeYmUrl = url => {
  if (typeof url !== "string") return url
  return url.startsWith("/") ? url : `/${url}`
}

const ensureYandexYmHitShim = () => {
  if (typeof window === "undefined") return

  // Retry a few times in case the Yandex inline snippet loads after Gatsby.
  // Keep this bounded to avoid infinite timers.
  const maxRetries = 20
  const retryDelayMs = 100
  window.__ongoYmHitShimRetries = window.__ongoYmHitShimRetries || 0

  const ymFn = window.ym
  if (typeof ymFn !== "function") {
    if (window.__ongoYmHitShimRetries < maxRetries) {
      window.__ongoYmHitShimRetries += 1
      window.setTimeout(ensureYandexYmHitShim, retryDelayMs)
    }
    return
  }
  if (typeof window.ym.hit === "function") return

  // gatsby-plugin-yandex-metrica incorrectly calls `window.ym.hit(url)`.
  // The actual API is `ym(counterId, 'hit', url)`.
  window.ym.hit = url => {
    try {
      const targetUrl = normalizeYmUrl(url)
      if (!Number.isFinite(YANDEX_METRICA_ID)) return
      ymFn(YANDEX_METRICA_ID, "hit", targetUrl)
    } catch (e) {
      // Never crash the app due to analytics.
    }
  }
}

const normalizePath = path => (path || "").replace(/\/$/, "") || "/"

let pendingPathname = null
let overlaySafetyTimeoutId = null

const setRouteLoadingOverlayActive = active => {
  if (typeof document === "undefined") return
  document.body.classList.toggle("ongo-route-loading-active", !!active)
  const overlay = document.getElementById("ongo-route-loading-overlay")
  if (overlay) overlay.setAttribute("aria-hidden", active ? "false" : "true")
}

const clearOverlaySafetyTimeout = () => {
  if (typeof window === "undefined") return
  if (overlaySafetyTimeoutId) {
    window.clearTimeout(overlaySafetyTimeoutId)
    overlaySafetyTimeoutId = null
  }
}

const startRouteLoadingOverlay = toPathname => {
  if (typeof window === "undefined") return
  pendingPathname = normalizePath(toPathname)
  setRouteLoadingOverlayActive(true)
  clearOverlaySafetyTimeout()

  // Safety fallback: never keep the UI blocked indefinitely.
  overlaySafetyTimeoutId = window.setTimeout(() => {
    pendingPathname = null
    setRouteLoadingOverlayActive(false)
    overlaySafetyTimeoutId = null
  }, 60000)
}

const stopRouteLoadingOverlayAfterPaint = () => {
  if (typeof window === "undefined") return
  clearOverlaySafetyTimeout()
  // Wait for at least one paint after route update.
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      pendingPathname = null
      setRouteLoadingOverlayActive(false)
    })
  })
}

// Frame-busting script to prevent clickjacking
export const onClientEntry = () => {
  // Ensure Yandex tracking shim exists before the first route update.
  ensureYandexYmHitShim()

  // Frame-busting script to prevent clickjacking (also protected by headers).
  // Guard for cross-origin access errors.
  try {
    if (window.top !== window.self) {
      window.top.location = window.self.location
    }
  } catch (e) {
    // Ignore: cross-origin framing blocks access to window.top
  }

  // Expose a stable API for components to trigger the overlay immediately.
  window.ongoStartRouteLoadingOverlay = startRouteLoadingOverlay
}

export const onPreRouteUpdate = () => {
  NProgress.start()
}

export const onRouteUpdate = ({ location, prevLocation }) => {
  NProgress.done()

  // Yandex metrica plugin bug workaround (prevents prod console error).
  ensureYandexYmHitShim()

  const nextPath = normalizePath(location?.pathname)
  const prevPath = normalizePath(prevLocation?.pathname)

  // Only stop the overlay when we actually changed routes.
  // This prevents premature hiding when Gatsby triggers updates
  // that don't correspond to a new pathname.
  if (nextPath !== prevPath) {
    // If we have a pending target, prefer waiting for it.
    if (!pendingPathname || normalizePath(pendingPathname) === nextPath) {
      stopRouteLoadingOverlayAfterPaint()
    }
  }
}
